import axios from 'axios';
import { CrudService } from './crud.service';

export class ExceptionService {
  constructor(private readonly crudService = new CrudService<any>('api/assortments')) {}

  public async uploadFile(season: string, exceptionType: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('season', season);
    formData.append('exceptionType', String(exceptionType));

    return await this.crudService.downloadFile('exceptions/import', formData, '', {
      responseType: 'blob',
    });
  }

  public async getRegionalExceptionReport(season: string): Promise<void> {
    if (!season) {
      throw new Error('Season is required to download the regional exception report.');
    }

    return await this.crudService.downloadFile(
      `exceptions/regional-report?season=${season}`,
      null,
      `regional-exception-report-${season}.xlsx`,
      {
        responseType: 'blob',
      },
    );
  }

  public async getSeasonalExceptionReport(season: string): Promise<void> {
    if (!season) {
      throw new Error('Season is required to download the seasonal exception report.');
    }

    try {
      // Filename is determined by server response (can be .csv or .zip depending on data size)
      return await this.crudService.downloadFile(`exceptions/seasonal-report?season=${season}`, null, undefined, {
        responseType: 'blob',
        timeout: 10 * 60 * 1000, // 10 minutes
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
    } catch (error) {
      throw await this.parseServiceError(error);
    }
  }

  private async parseServiceError(error: unknown): Promise<Error> {
    if (!axios.isAxiosError(error) || !error.response) {
      return error instanceof Error ? error : new Error('An unexpected error occurred');
    }

    const { status, data } = error.response;

    // When responseType is 'blob', error response body is a Blob that needs parsing
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);

        return new ServiceError(parsed.message || 'An unexpected error occurred', status);
      } catch {
        return new ServiceError('An unexpected error occurred', status);
      }
    }

    const message = typeof data?.message === 'string' ? data.message : 'An unexpected error occurred';

    return new ServiceError(message, status);
  }
}

export class ServiceError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
  ) {
    super(message);
    this.name = 'ServiceError';
  }
}
